// Composition API для страницы товара
import type { IProduct } from '../models/product';
import { productMapper } from '../mappers/product';
import { Product } from '../models/product-legacy';
import { productToSkuList } from '../mappers/product-to-sku-list';
import type { ISkuItem } from '../mappers/product-to-sku-list';
import type { IBanner } from '../models/banner';
import { ref, watch, reactive, computed } from 'vue';
import { actionsToBanners } from '../mappers/actions-to-banners';
import type { TSkuCharacteristic, TSkuSelectedIds } from '../models/sku-characteristic';
import { mapCartItem } from '../mappers/map-cart-item';
import { characteristicsToDisabledCharacteristic } from '../mappers/characteristics-to-disabled-characteristic';
import { productToBadges } from '../mappers/product-to-badges';
import { productToCategories, type TSkuCategory } from '../mappers/product-to-categories';
import { productToSkuSelectorData } from '../mappers/product-to-sku-selector-data';
import type { IBadge } from '../models/badge';
import { getProductPage } from '@/shared/apollo-api/api/product-page';
import type {
  ProductPageQuery,
  FeedbackSortType,
  Feedback,
  FeedbackFilterType,
} from '@/shared/apollo-api/__generated__/types';
import { productCharacteristicMapper } from '../mappers/product-characteristic';
import type { ICharacteristics } from '../models/characteristic';
import { productToFilters } from '../mappers/product-to-filters';
import { getCurrentSkuPhoto } from './getCurrentSkuPhoto';
import debounce from 'lodash.debounce';
import { AnalyticAPI } from './analytics';
import { type IProductEntity, ProductMM } from '@/entities';
import { useCart, useCityStore, useFavorites } from '@/features/store';
import { buildAnalyticsParams } from '@/features/store/favorite/business/analyticsParams';
import { useProductPageTracker } from '../trackers/useProductPageTracker';
import { productService } from '@/shared/api/ke-gateway/product/productService';
import { EProductOptionDtoTypes } from '@/pages/index/-src/shared/types/product';
import type { IProductOptionInfo } from '@/pages/index/-src/shared/types/product';

// TODO: Import KOPECKS_IN_RUB from proper location
const KOPECKS_IN_RUB = 100;

interface IProductPageState {
  product: IProduct | null;
  skuList: ISkuItem[];
  currentSku: ISkuItem | null;
  banners: IBanner[];
  skuCharacteristics: TSkuCharacteristic[];
  isFavorite: boolean;
  skuGroupId: string | null;
  error: boolean;
  loading: boolean;
  characteristics: ICharacteristics | undefined;
  tabs: Array<{ value: string; type: string; count?: number }>;
  reviewsPhotoAmount: number;
  currentTabSlug: string;
  sortAnswers: FeedbackSortType;
  answerOnlyPhoto: FeedbackFilterType[];
  feedbacks: Feedback[];
  feedbackLoading: boolean;
  bundle: any;
  productOptionInfo: Map<number, IProductOptionInfo>;
}

const defaultState: IProductPageState = {
  product: null,
  banners: [],
  skuCharacteristics: [],
  currentSku: null,
  skuList: [],
  isFavorite: false,
  skuGroupId: null,
  error: false,
  loading: false,
  characteristics: undefined,
  tabs: [],
  reviewsPhotoAmount: 0,
  currentTabSlug: '',
  sortAnswers: 'Relevance' as FeedbackSortType,
  answerOnlyPhoto: [],
  feedbacks: [],
  feedbackLoading: false,
  bundle: null,
  productOptionInfo: new Map(),
};

// Глобальное реактивное состояние
const state = reactive<IProductPageState>({ ...defaultState });
const skuQntyUiState = ref(0);

// Composition functions
const { currentCityId } = useCityStore();
const { getProductLiteBySkuId, updateProductCartLiteQnty, cartQnty } = useCart();
const { handleFavoriteToggle } = useFavorites();

const findMinPriceSku = (skuList: ISkuItem[]) => {
  return skuList
    .slice()
    .sort((a, b) => {
      if (a.availableAmount > 0 && b.availableAmount > 0) {
        return a.purchasePrice - b.purchasePrice;
      }
      return a.availableAmount - b.availableAmount;
    })
    .pop();
};

const computeCurrentSku = (
  skuId?: number,
  skuOption?: string,
  optionValue?: string
): ISkuItem | null => {
  const skuList = state.skuList || [];

  // Тут фолбечимся на старые параметры страницы, чтобы старые ссылки не ломались
  if (!skuId && skuOption && optionValue) {
    const foundSku: ISkuItem | undefined = findMinPriceSku(
      skuList.filter((sku) =>
        sku.characteristics.find(
          (characteristic) =>
            characteristic.characteristicId === skuOption &&
            characteristic.value.valueId === optionValue
        )
      )
    );

    skuId = foundSku?.id || 0;
  }

  let currentSku: ISkuItem | undefined = skuId
    ? skuList.find((sku) => sku.id === skuId)
    : undefined;

  if (!currentSku) currentSku = findMinPriceSku(skuList);

  return currentSku || null;
};

const updateCurrentSkuCount = () => {
  const currentSkuID = state.currentSku?.id?.toString();
  if (typeof currentSkuID === 'undefined') return;

  const currentItem = getProductLiteBySkuId(currentSkuID);
  skuQntyUiState.value = currentItem?.product.amount || 0;
};

const setProduct = async (product: ProductPageQuery, skuId?: number) => {
  state.product = productMapper(product.product);
  state.isFavorite = product.product.favorite;
  state.skuList = productToSkuList(product.product);
  state.skuCharacteristics = productCharacteristicMapper(product.product);
  state.currentSku = computeCurrentSku(skuId || state.currentSku?.id || undefined);
  updateCurrentSkuCount();
  state.reviewsPhotoAmount = product.productPage?.reviewsPhotoAmount || 0;
  const actions = product.productPage.actions;
  state.banners = actionsToBanners(actions || []);
  state.characteristics = productToFilters(product.product);
  state.loading = false;

  await loadProductOptionInfo(state.currentSku);

  // Настройка табов
  try {
    state.currentTabSlug = '';
    state.tabs = product.product.comments.map((comment) => ({
      value: comment.value,
      type: comment.type,
    }));
  } catch (e) {
    console.error('Error setting tabs:', e);
  }
};

const loadProductOptionInfo = async (currentSku: ISkuItem | null): Promise<void> => {
  if (!currentSku?.productOptionDtos) {
    return;
  }

  const bnplOptions = currentSku.productOptionDtos.filter(
    (option) =>
      option.type === EProductOptionDtoTypes.DOLYAME || option.type === EProductOptionDtoTypes.PODELI
  );

  if (bnplOptions.length === 0) {
    return;
  }

  const optionIds = Array.from(new Set(bnplOptions.map((option) => option.optionId)));

  try {
    const response = await productService.getOptionInfo(optionIds, [currentSku.id]);
    const optionInfoList = response?.data || (Array.isArray(response) ? response : null);

    if (optionInfoList?.length) {
      optionInfoList.forEach((optionInfo) => {
        state.productOptionInfo.set(optionInfo.optionId, optionInfo);
      });
    }
  } catch (e) {
    console.error('Failed to load product option info:', e);
  }
};

export const useLoadProductPage = async (productId: string, skuId?: number) => {
  const { data, error, status } = await useAsyncData(`product-page-${productId}`, () =>
    getProductPage(Number(productId), currentCityId.value).then((r) => {
      if (r.errors?.length) {
        throw new Error(r.errors?.[0]?.message);
      }

      return r.data;
    })
  );

  if (status.value === 'success' && data.value) {
    setProduct(data.value, skuId);
  }

  return { data, error, status };
};

export const useProductPage = () => {
  const subscriptions: (() => void)[] = [];
  const { trackProductPageView } = useProductPageTracker();

  // Computed свойства
  const isOutdated = computed(() => {
    // TODO: Implement city change detection
    return false;
  });

  const categories = computed(() => {
    return state.product ? productToCategories(state.product) : [];
  });

  // Computed для данных SKU Selector
  const skuSelectorData = computed(() => {
    if (!state.product) {
      return { products: [], characteristics: [] };
    }
    return productToSkuSelectorData(state.product as any);
  });

  // Методы для работы с состоянием
  const updateCurrentSkuCount = () => {
    const currentSkuID = state.currentSku?.id?.toString();
    if (typeof currentSkuID === 'undefined') return;

    const currentItem = getProductLiteBySkuId(currentSkuID);
    skuQntyUiState.value = currentItem?.product.amount || 0;
  };

  const findMinPriceSku = (skuList: ISkuItem[]) => {
    return skuList
      .slice()
      .sort((a, b) => {
        if (a.availableAmount > 0 && b.availableAmount > 0) {
          return a.purchasePrice - b.purchasePrice;
        }
        return a.availableAmount - b.availableAmount;
      })
      .pop();
  };

  const computeCurrentSku = (
    skuId?: number,
    skuOption?: string,
    optionValue?: string
  ): ISkuItem | null => {
    const skuList = state.skuList || [];

    // Тут фолбечимся на старые параметры страницы, чтобы старые ссылки не ломались
    if (!skuId && skuOption && optionValue) {
      const foundSku: ISkuItem | undefined = findMinPriceSku(
        skuList.filter((sku) =>
          sku.characteristics.find(
            (characteristic) =>
              characteristic.characteristicId === skuOption &&
              characteristic.value.valueId === optionValue
          )
        )
      );

      skuId = foundSku?.id || 0;
    }

    let currentSku: ISkuItem | undefined = skuId
      ? skuList.find((sku) => sku.id === skuId)
      : undefined;

    if (!currentSku) currentSku = findMinPriceSku(skuList);

    return currentSku || null;
  };

  const setProduct = async (product: ProductPageQuery) => {
    state.product = productMapper(product.product);
    state.isFavorite = product.product.favorite;
    state.skuList = productToSkuList(product.product);
    state.skuCharacteristics = productCharacteristicMapper(product.product);
    state.currentSku = computeCurrentSku(state.currentSku?.id || undefined);
    updateCurrentSkuCount();
    state.reviewsPhotoAmount = product.productPage?.reviewsPhotoAmount || 0;
    const actions = product.productPage.actions;
    state.banners = actionsToBanners(actions || []);
    state.characteristics = productToFilters(product.product);
    state.loading = false;

    await loadProductOptionInfo(state.currentSku);

    // Настройка табов
    try {
      state.currentTabSlug = '';
      state.tabs = product.product.comments.map((comment) => ({
        value: comment.value,
        type: comment.type,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  // Основные методы
  // @ deprecated
  const updateProductState = async (
    productId: number,
    skuId: number,
    skuOption?: string,
    optionValue?: string
  ) => {
    let result: ProductPageQuery;

    try {
      state.loading = true;
      const gqlResponse = await getProductPage(productId, currentCityId.value);
      result = gqlResponse.data as unknown as ProductPageQuery;
      state.error = !result;
    } catch (e) {
      state.loading = false;
      state.error = true;
      return;
    }

    state.product = productMapper(result.product);
    state.isFavorite = result.product.favorite;
    state.skuList = productToSkuList(result.product);
    state.skuCharacteristics = productCharacteristicMapper(result.product);
    const currentSku = computeCurrentSku(skuId, skuOption, optionValue);

    state.currentSku = currentSku;
    updateCurrentSkuCount();

    state.reviewsPhotoAmount = result.productPage?.reviewsPhotoAmount || 0;
    const actions = result.productPage.actions;

    state.banners = actionsToBanners(actions || []);
    state.characteristics = productToFilters(result.product);

    state.loading = false;

    // Настройка табов
    try {
      state.currentTabSlug = '';
      state.tabs = result.product.comments.map((comment) => ({
        value: comment.value,
        type: comment.type,
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const handleRouteChange = async (to: any) => {
    if (to.name === 'product-not-found') {
      state.error = true;
      return;
    }
    if (to.name !== 'product') return;

    const skuIdQueryParam = to.query.skuId ?? to.query.skuid;
    const skuId = Array.isArray(skuIdQueryParam)
      ? Number(skuIdQueryParam[0])
      : Number(skuIdQueryParam);
    const productId = parseInt(to.params.id?.replace(/[^\d]+/g, ''));
    const skuGroupId = to.query[Product.ESKUGroupType.SkuGroup];
    const skuOption = String(to.query[Product.ESKUGroupType.SkuOption]);
    const optionValue = String(to.query[Product.ESKUGroupType.OptionValue]);

    if (state.product?.id !== productId) {
      await updateProductState(productId, skuId, skuOption, optionValue);
    }

    state.skuGroupId = skuGroupId ? String(skuGroupId) : null;

    if (state.currentSku?.id !== skuId) {
      if (skuId) {
        state.currentSku = computeCurrentSku(skuId);
        updateCurrentSkuCount();
      }
    }
  };

  const selectSku = async (id: number, router: any, isScrollToTop = false) => {
    const scroll = window.scrollY;

    // Обновляем currentSku напрямую (не ждем изменения URL и handleRouteChange)
    // Это необходимо для мгновенного обновления UI (цена, галерея)
    state.currentSku = computeCurrentSku(id);
    updateCurrentSkuCount();
    
    // Загружаем информацию о BNPL опциях для нового SKU
    await loadProductOptionInfo(state.currentSku);

    // Обновляем URL для синхронизации состояния с браузером
    await router
      .replace({ query: { skuId: String(id) } })
      .catch(() => {})
      .then(() => {
        if (isScrollToTop) return;
        window.scroll(0, scroll);
      });
  };

  const onCharacteristicChange = (characteristicId: string, valueId: string, router: any) => {
    if (!state.currentSku) return;

    const currentSelectedIds = getSelectedIds(state.currentSku);
    const selectedIds = {
      ...currentSelectedIds,
      [characteristicId]: valueId,
    };

    const skuList: ISkuItem[] = state.skuList || [];

    // Ищем ску, которое совпадает со всем ранее выбранными характеристиками
    let currentSku = skuList.find((sku) =>
      sku.characteristics.every(
        (characteristic) =>
          selectedIds[characteristic.characteristicId] === characteristic.value.valueId
      )
    );

    // Если не нашли, ищем первое ску с последней выбранной характеристикой
    if (!currentSku) {
      currentSku = skuList.find((sku) =>
        sku.characteristics.find(
          (characteristic) =>
            characteristic.characteristicId === characteristicId &&
            characteristic.value.valueId === valueId
        )
      );
    }

    if (currentSku) {
      selectSku(currentSku.id!, router);
    }
  };

  const sendCurrentSkuAmount = async (amount: number) => {
    if (!state.currentSku) return;

    const cartItem = getProductLiteBySkuId(state.currentSku.id!.toString());
    const cartItemAmount = cartItem?.product.amount ?? 0;

    if (amount === cartItemAmount) return;

    const productMM = {
      skuId: state.currentSku.id!.toString(),
      id: state.product?.id?.toString(),
      title: state.product?.title,
      fullPrice: state.currentSku.fullPrice,
      sellPrice: state.currentSku.purchasePrice,
    };

    await updateProductCartLiteQnty(productMM, amount);
  };

  const updateCartQntyDebounced = debounce(sendCurrentSkuAmount, 300);

  const setCurrentSkuAmount = async (amount: number): Promise<void> => {
    if (!state.currentSku) return;

    amount = Math.max(0, Math.min(amount, state.currentSku.availableAmount));

    if (!skuQntyUiState.value) {
      return sendCurrentSkuAmount(amount).then(() => {
        skuQntyUiState.value = amount;
      });
    }

    skuQntyUiState.value = amount;
    updateCartQntyDebounced(amount);
  };

  const buySingle = async (router: any): Promise<void> => {
    if (!state.currentSku) return;

    AnalyticAPI.sendAnalyticsBuyInOneClick({
      quantity: state.currentSku.availableAmount,
      sell_price: state.currentSku.purchasePrice,
      sku_id: state.currentSku.id,
    });

    await router.push({
      name: 'checkout',
      params: {
        items: JSON.stringify([mapCartItem(state.product!, state.currentSku!)]),
      },
    });
  };

  const likeProduct = async (): Promise<void> => {
    if (!state.product || !state.currentSku) return;

    const isFavorite = state.isFavorite;

    const productMM = new ProductMM({
      id: `${state.product.id}`,
      skuId: state.currentSku.id!.toString(),
      fullPrice: state.currentSku.fullPrice,
      sellPrice: state.currentSku.purchasePrice,
      isFavorite,
      rating: state.product.rating,
      feedbackQuantity: state.product.feedbackQuantity,
      badges: state.product.badges,
      externalRating: state.product.externalRating,
    });

    state.isFavorite = !isFavorite;

    const extraParams = await buildAnalyticsParams('productScreen', productMM.product, {});

    await handleFavoriteToggle(productMM.product, 'productScreen', extraParams);
  };

  const computeDisabledCharacteristics = (
    characteristics: TSkuCharacteristic[],
    currentSku: ISkuItem,
    skuList: ISkuItem[]
  ): TSkuCharacteristic[] => {
    return characteristicsToDisabledCharacteristic(characteristics, currentSku, skuList);
  };

  const getSelectedIds = (sku: ISkuItem): TSkuSelectedIds => {
    return sku.characteristics.reduce(
      (sum, characteristic) => ({
        ...sum,
        [characteristic.characteristicId]: characteristic.value.valueId,
      }),
      {}
    );
  };

  const getProductBadges = (product: IProduct): IBadge[] => {
    return productToBadges(product);
  };

  const getProductCategories = (product: IProduct): TSkuCategory[] => {
    return productToCategories(product);
  };

  // Feedback методы
  const changeSortAnswers = (sort: FeedbackSortType): void => {
    state.sortAnswers = sort;
    loadMoreFeedback();
  };

  const changeAnswerOnlyPhoto = (value: FeedbackFilterType[]): void => {
    state.answerOnlyPhoto = value;
  };

  const changeFeedbacks = (feedbacks: Feedback[]): void => {
    state.feedbacks = feedbacks;
  };

  const changeFeedbackLoading = (data: boolean): void => {
    state.feedbackLoading = data;
  };

  const changeSortAndPhotoFeedback = (sort: FeedbackSortType, photo: FeedbackFilterType[]) => {
    changeFeedbacks([]);
    changeAnswerOnlyPhoto(photo);
    changeSortAnswers(sort);
  };

  const getFeedback = async (page: number, size: number): Promise<Feedback[]> => {
    // TODO: Implement productPageFeedback API
    console.warn('productPageFeedback API not implemented yet');
    return [];
  };

  const loadMoreFeedback = async () => {
    if (state.feedbacks.length >= (state.product?.feedbackQuantity || 0) || state.feedbackLoading) {
      return;
    }

    const PAGE_SIZE = 10;
    const page = Math.ceil(state.feedbacks.length / PAGE_SIZE);

    changeFeedbackLoading(true);
    const resultFeedback = await getFeedback(page, PAGE_SIZE);
    changeFeedbackLoading(false);
    state.feedbacks.push(...resultFeedback);
  };

  // Lifecycle методы
  const onProductPageEnter = () => {
    if (state.product && state.currentSku) {
      const productBadges = getProductBadges(state.product);
      const badgesLabels = productBadges.map((badge) => badge.label).join(';');
      const badgesId = productBadges[0]?.id || 0;

      trackProductPageView({
        productId: String(state.product.id),
        productName: state.product.title,
        categoryId: String(state.product.categoryId || state.product.category?.id),
        categoryName: state.product.category?.title,
        price: state.currentSku.purchasePrice,
        oldPrice: state.currentSku.fullPrice,
        brand: state.product.seller?.title,
        skuId: String(state.currentSku.id),
        skuGroupId: String(state.currentSku.skuGroupId || state.skuGroupId),
        isFavorite: state.isFavorite,
        rating: state.product.rating,
        reviewsCount: state.product.feedbackQuantity,
        availableAmount: state.currentSku.availableAmount,
        photos: state.product.photos,
        badges: badgesLabels,
        badgesId: badgesId,
        sellerId: state.product.seller?.id,
        externalRating: state.product.externalRating,
        video: state.currentSku.video || state.product.video,
        currentSku: state.currentSku,
        characteristics: state.skuCharacteristics,
      });
    }

    const cartWatch = watch(cartQnty, () => updateCurrentSkuCount());
    subscriptions.push(() => cartWatch());

    const currentSkuWatch = watch(
      () => [state.currentSku?.id],
      () => {
        const currentSku = state.currentSku;
        const product = state.product!;

        if (!currentSku || !product) {
          return;
        }

        const price = currentSku?.purchasePrice
          ? +(currentSku.purchasePrice / KOPECKS_IN_RUB).toFixed(2)
          : undefined;
        const oldPrice = currentSku?.fullPrice
          ? +(currentSku.fullPrice / KOPECKS_IN_RUB).toFixed(2)
          : undefined;

        console.log('skuView tracking event:', {
          products: [
            {
              categoryId: String(product.categoryId || product.category?.id),
              badges: product.badges,
              price,
              oldPrice,
              id: String(product.id),
            },
          ],
          other: {
            skuId: currentSku.id,
            sku_group_id: currentSku.skuGroupId || state.skuGroupId,
            pageType: 'Product',
          },
        });
      }
    );

    const productIdWatch = watch(
      () => state.product?.id,
      () => {
        const product = state.product;
        if (!product) return;

        const favorites: any[] = [];
        const isFavourite = Boolean(
          favorites && favorites.find((el: any) => product && el.productId === product.id)
        );
        const seller_rates_number = product.seller?.reviews ? product.seller.reviews : null;
        const currentSku = state.currentSku;
        const skuGroupId = state.skuGroupId;

        const price = currentSku?.purchasePrice
          ? +(currentSku.purchasePrice / KOPECKS_IN_RUB).toFixed(2)
          : 0;
        const oldPrice = currentSku?.fullPrice
          ? +(currentSku.fullPrice / KOPECKS_IN_RUB).toFixed(2)
          : 0;

        const photos = getCurrentSkuPhoto(currentSku, product.photos);

        console.log('viewProduct tracking event:', {
          other: {
            sku_group_id: skuGroupId,
            is_favourite: isFavourite,
            pageType: 'Product',
            product_rating: product.rating,
            sellerId: product.seller?.id,
            seller_rates_number,
            seller_rating: product.seller.rating,
            orders_number: product.ordersAmount,
            available_sku_ids:
              state.skuList?.filter((sku) => sku.availableAmount > 0).map((sku) => sku.id) || [],
            rev_num: state.product?.feedbackQuantity,
            is_parsing_rating: state.product?.externalRating,
          },
          products: [
            {
              categoryId: String(product.category.id),
              category: product.category.title,
              id: String(product.id),
              price,
              oldPrice,
              amount: product.totalAvailableAmount,
              title: product.title,
              brand: product.seller.title,
              image: product.photos?.length ? product.photos[0].high : '',
              sku: product.skuList.map((sku) => String(sku.id)),
              variant: currentSku?.id ? String(currentSku?.id) : null,
              currency: 'RUB',
              badges: product.badges,
            },
          ],
        });
      }
    );

    subscriptions.push(
      () => currentSkuWatch(),
      () => productIdWatch()
    );
  };

  const onProductPageLeave = () => {
    // Сброс состояния
    Object.assign(state, defaultState);

    // Отписка от всех watchers
    subscriptions.forEach((unsubscribe) => unsubscribe());
    subscriptions.length = 0;
  };

  const productEntity = computed(
    () =>
      ({
        title: state.product?.title,
        fullPrice: state.currentSku?.fullPrice,
        sellPrice: state.currentSku?.purchasePrice,
        badges: state.product?.badges,
        id: state.product?.id.toString(),
        rating: state.product?.rating,
        feedbackQuantity: state.product?.feedbackQuantity,
        photo: {
          link: {
            high: state.product?.photos[0]?.high,
            low: state.product?.photos[0]?.low,
          },
        },
        availableAmount: state.product?.totalAvailableAmount,
        inStock: true,
      }) satisfies IProductEntity
  );

  // Возвращаем все необходимые функции и состояние
  return {
    // Состояние
    state: readonly(state),
    productEntity,
    setProduct,
    skuQntyUiState,

    // Computed
    isOutdated,
    categories,
    skuSelectorData,

    // Основные методы
    updateProductState,
    setCurrentTabSlug: (slug: string) => {
      state.currentTabSlug = slug;
    },
    handleRouteChange,
    selectSku,
    onCharacteristicChange,
    setCurrentSkuAmount,
    buySingle,
    likeProduct,

    // Вспомогательные методы
    computeDisabledCharacteristics,
    getSelectedIds,
    getProductBadges,
    getProductCategories,

    // Feedback методы
    changeSortAnswers,
    changeAnswerOnlyPhoto,
    changeFeedbacks,
    changeFeedbackLoading,
    changeSortAndPhotoFeedback,
    loadMoreFeedback,

    // Getters для обратной совместимости
    getState: () => state,
    getSortAnswers: () => state.sortAnswers,
    getAnswerOnlyPhoto: () => state.answerOnlyPhoto,
    getBooleanOnlyPhoto: () => state.answerOnlyPhoto.length !== 0,
    getFeedBacks: () => state.feedbacks,
    getFeedbackLoading: () => state.feedbackLoading,

    // Lifecycle
    onProductPageEnter,
    onProductPageLeave,
  };
};
